// src/components/useSpeechRecognition.js
import { useMemo, useRef, useState, useCallback } from "react";

/**
 * Hook para reconhecimento de voz com sessão persistente.
 * - Mantém o ditado ativo até o utilizador parar manualmente.
 * - Se o motor terminar sozinho, reinicia automaticamente.
 * - Evita duplicação de frases usando controlo por delta do transcript final.
 *
 * @param {(texto: string) => void} onTextoReconhecido
 * @param {{
 *   lang?: string,
 *   interimResults?: boolean,
 *   continuous?: boolean
 * }} opts
 */
export function useSpeechRecognition(onTextoReconhecido, opts = {}) {
  const { lang = "pt-PT", interimResults = false, continuous = true } = opts;

  const suporteVoz = useMemo(
    () =>
      typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition),
    []
  );

  const [reconhecendo, setReconhecendo] = useState(false);
  const [erro, setErro] = useState(null);

  const recogRef = useRef(null);
  const stopManualRef = useRef(false);
  const startingRef = useRef(false);

  // transcript final acumulado ao longo de toda a sessão do utilizador
  const transcriptGlobalRef = useRef("");

  // último transcript final já processado dentro da instância atual do recognizer
  const lastSessionFinalRef = useRef("");

  const normalizar = (texto) => (texto || "").replace(/\s+/g, " ").trim();

  const obterOverlap = (base, extra) => {
    const max = Math.min(base.length, extra.length);
    for (let i = max; i > 0; i--) {
      if (base.slice(-i) === extra.slice(0, i)) return i;
    }
    return 0;
  };

  const acrescentarDelta = (base, novo) => {
    const a = normalizar(base);
    const b = normalizar(novo);

    if (!b) return a;
    if (!a) return b;
    if (a === b) return a;
    if (a.endsWith(b)) return a;
    if (b.startsWith(a)) return b;

    const overlap = obterOverlap(a, b);
    if (overlap > 0) {
      return `${a}${b.slice(overlap)}`.trim();
    }

    return `${a} ${b}`.trim();
  };

  const capitalizarLinhas = (texto) => {
    if (!texto) return texto;

    return texto
      .split("\n")
      .map((linha) => {
        const l = linha.trim();
        if (!l) return "";
        return l.charAt(0).toUpperCase() + l.slice(1);
      })
      .join("\n");
  };

  const processarTextoDitado = (texto) => {
    if (!texto) return texto;

    let t = texto;

    // "ponto" passa a ponto final + mudança de linha
    t = t.replace(/\b(ponto final|ponto)\b/gi, ".\n");

    // opcionais
    t = t.replace(/\bvírgula\b/gi, ",");
    t = t.replace(/\b(dois pontos)\b/gi, ":");
    t = t.replace(/\b(ponto e vírgula)\b/gi, ";");

    // limpar espaços antes de pontuação
    t = t.replace(/\s+([,.;:])/g, "$1");

    // limpar espaços à volta das mudanças de linha
    t = t.replace(/[ \t]*\n[ \t]*/g, "\n");

    // evitar linhas vazias repetidas
    t = t.replace(/\n{2,}/g, "\n");

    return capitalizarLinhas(t.trim());
  };

  const entregarTextoFinal = useCallback(() => {
    let finalText = normalizar(transcriptGlobalRef.current);

    transcriptGlobalRef.current = "";
    lastSessionFinalRef.current = "";

    if (!finalText) return;

    finalText = processarTextoDitado(finalText);

    if (typeof onTextoReconhecido === "function") {
      onTextoReconhecido(finalText);
    }
  }, [onTextoReconhecido]);

  const criarRecognizer = useCallback(() => {
    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new Ctor();

    rec.lang = lang;
    rec.interimResults = interimResults;
    rec.maxAlternatives = 1;
    rec.continuous = continuous;

    rec.onstart = () => {
      startingRef.current = false;
      setReconhecendo(true);
      setErro(null);

      // nova instância -> nova sessão interna do motor
      lastSessionFinalRef.current = "";
    };

    rec.onresult = (event) => {
      // reconstruir o transcript final atual desta instância
      let sessionFinal = "";

      for (let i = 0; i < event.results.length; i++) {
        const r = event.results[i];
        if (!r?.isFinal) continue;

        const part = normalizar(r[0]?.transcript || "");
        if (!part) continue;

        sessionFinal = sessionFinal ? `${sessionFinal} ${part}` : part;
      }

      sessionFinal = normalizar(sessionFinal);
      if (!sessionFinal) return;

      const lastProcessed = normalizar(lastSessionFinalRef.current);

      // se o motor repetiu exatamente o mesmo estado final, ignorar
      if (sessionFinal === lastProcessed) return;

      let delta = "";

      if (!lastProcessed) {
        delta = sessionFinal;
      } else if (sessionFinal.startsWith(lastProcessed)) {
        delta = normalizar(sessionFinal.slice(lastProcessed.length));
      } else {
        // fallback quando o motor resegmenta a frase de forma diferente
        const overlap = obterOverlap(lastProcessed, sessionFinal);
        delta = overlap > 0 ? normalizar(sessionFinal.slice(overlap)) : sessionFinal;
      }

      if (delta) {
        transcriptGlobalRef.current = acrescentarDelta(transcriptGlobalRef.current, delta);
      }

      lastSessionFinalRef.current = sessionFinal;
    };

    rec.onerror = (ev) => {
      const errorCode = ev?.error || "unknown";

      // erros benignos; deixar o onend decidir o restart
      if (errorCode === "no-speech" || errorCode === "aborted") {
        return;
      }

      setErro(errorCode);

      if (
        errorCode === "audio-capture" ||
        errorCode === "not-allowed" ||
        errorCode === "service-not-allowed"
      ) {
        stopManualRef.current = true;
        setReconhecendo(false);
        try {
          rec.stop();
        } catch {}
      }
    };

    rec.onend = () => {
      recogRef.current = null;
      startingRef.current = false;

      if (stopManualRef.current) {
        setReconhecendo(false);
        stopManualRef.current = false;
        entregarTextoFinal();
        return;
      }

      // terminou sozinho -> reiniciar sem perder o acumulado global
      try {
        const novo = criarRecognizer();
        recogRef.current = novo;
        startingRef.current = true;
        novo.start();
      } catch {
        setReconhecendo(false);
        setErro("restart-failed");
      }
    };

    return rec;
  }, [lang, interimResults, continuous, entregarTextoFinal]);

  const iniciar = useCallback(() => {
    if (!suporteVoz || reconhecendo || startingRef.current || recogRef.current) return;

    try {
      stopManualRef.current = false;
      transcriptGlobalRef.current = "";
      lastSessionFinalRef.current = "";
      setErro(null);

      const rec = criarRecognizer();
      recogRef.current = rec;
      startingRef.current = true;
      rec.start();
    } catch {
      startingRef.current = false;
      setErro("start-failed");
      setReconhecendo(false);
      recogRef.current = null;
    }
  }, [suporteVoz, reconhecendo, criarRecognizer]);

  const parar = useCallback(() => {
    const rec = recogRef.current;
    if (!rec) return;

    stopManualRef.current = true;

    try {
      rec.stop();
    } catch {
      setReconhecendo(false);
      stopManualRef.current = false;
      entregarTextoFinal();
      recogRef.current = null;
    }
  }, [entregarTextoFinal]);

  return { iniciar, parar, reconhecendo, suporteVoz, erro };
}
